import { useSeo } from "@/lib/seo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PropertyCard from "@/components/PropertyCard";
import { Heart, Bell, Trash2, BellOff } from "lucide-react";

export default function Favorites() {
    useSeo({ title: "Preferiti e ricerche salvate | VendiDirect", description: "Gli immobili che hai salvato e le tue ricerche con alert email.", noindex: true });
    const { user } = useAuth();
    const [tab, setTab] = useState("favorites");
    const [favs, setFavs] = useState([]);
    const [searches, setSearches] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([
                api.get("/favorites").catch(() => ({ data: { items: [] } })),
                user ? api.get("/saved-searches").catch(() => ({ data: { items: [] } })) : Promise.resolve({ data: { items: [] } }),
            ]);
            setFavs(a.data.items || []);
            setSearches(b.data.items || []);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchAll(); }, [user]);

    const removeSearch = async (id) => {
        await api.delete(`/saved-searches/${id}`);
        fetchAll();
    };

    return (
        <div data-testid="favorites-page" className="pt-32 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28">
            <span className="label-eyebrow">Area personale</span>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink-50 mt-3">Preferiti & Ricerche</h1>

            <div className="flex gap-2 mt-10 mb-10 border-b border-white/10">
                <TabBtn active={tab === "favorites"} onClick={() => setTab("favorites")} testid="tab-favorites">
                    <Heart size={14} /> Preferiti ({favs.length})
                </TabBtn>
                <TabBtn active={tab === "searches"} onClick={() => setTab("searches")} testid="tab-searches">
                    <Bell size={14} /> Ricerche salvate ({searches.length})
                </TabBtn>
            </div>

            {tab === "favorites" && (
                loading ? <div className="text-ink-400">Caricamento...</div> :
                favs.length === 0 ? <div className="border border-white/10 p-16 text-center text-ink-400">Nessun preferito ancora.</div> :
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {favs.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
                </div>
            )}

            {tab === "searches" && (
                loading ? <div className="text-ink-400">Caricamento...</div> :
                searches.length === 0 ? (
                    <div className="border border-white/10 p-16 text-center text-ink-400">
                        Non hai ancora salvato alcuna ricerca.<br />
                        <Link to="/annunci" className="inline-block mt-4 text-gold hover:underline">Esplora gli annunci →</Link>
                    </div>
                ) : (
                    <div className="border border-white/10 divide-y divide-white/5">
                        {searches.map((s) => (
                            <div key={s.id} data-testid={`saved-search-${s.id}`} className="p-6 grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-6">
                                    <div className="font-serif text-lg text-ink-50">{s.name}</div>
                                    <div className="text-xs text-ink-400 mt-1 flex flex-wrap gap-2">
                                        {s.city && <span className="px-2 py-0.5 border border-white/10">{s.city}</span>}
                                        {s.property_type && <span className="px-2 py-0.5 border border-white/10">{s.property_type}</span>}
                                        {s.min_price && <span className="px-2 py-0.5 border border-white/10">€{s.min_price}+</span>}
                                        {s.max_price && <span className="px-2 py-0.5 border border-white/10">≤€{s.max_price}</span>}
                                        {s.min_rooms && <span className="px-2 py-0.5 border border-white/10">{s.min_rooms}+ locali</span>}
                                    </div>
                                </div>
                                <div className="col-span-3 text-sm text-gold flex items-center gap-1.5">
                                    <Bell size={12} /> {s.match_count} match
                                </div>
                                <div className="col-span-3 flex justify-end gap-2">
                                    <Link to={`/annunci?${new URLSearchParams({
                                        ...(s.city && { city: s.city }),
                                        ...(s.property_type && { property_type: s.property_type }),
                                    }).toString()}`} className="px-4 py-2 text-xs border border-white/20 text-ink-100 hover:border-gold hover:text-gold">Esegui</Link>
                                    <button data-testid={`delete-search-${s.id}`} onClick={() => removeSearch(s.id)} className="p-2 text-ink-100 hover:text-red-400"><Trash2 size={14} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}
            {tab === "searches" && searches.length > 0 && (
                <p className="text-xs text-ink-400 mt-6 flex items-center gap-2"><BellOff size={11} /> Riceverai una email digest quando ci sono nuovi annunci che corrispondono.</p>
            )}
        </div>
    );
}

const TabBtn = ({ active, children, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid} className={`px-5 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors -mb-px ${active ? "border-gold text-gold" : "border-transparent text-ink-400 hover:text-ink-100"}`}>
        {children}
    </button>
);
